import React from "react";
import { toast } from "react-toastify";

import {
  KintoneExportReport,
  KintoneExportSetting,
  KintoneFormFields,
} from "@graffer-inc/cloud-gov-core";
import { IFormSchemaV2 } from "@graffer-inc/form-schema";
import { UpdateKintoneExportSettingInput } from "@graffer-inc/smart-apply";

import { validateUpdateSettingInput } from "../../../../../../application/domain/kintone-export";

import type { UpdateKintoneExportSettingFuncType } from "../../../../../pages/data-transfer/kintone-export/page-hooks/kintone-export-setting-detail/hook";
import { ManualExportSubmissionFuncType } from "../../../common-hook";

type UseSettingInfoOperationsProps = {
  kintoneExportSetting: KintoneExportSetting;
  formSchema: IFormSchemaV2;
  kintoneFormFields?: KintoneFormFields;
  onClickUpdate: UpdateKintoneExportSettingFuncType;
  onClickExport: ManualExportSubmissionFuncType;
  moveToReportDetail: (reportId: KintoneExportReport["id"]) => void;
};

export const useSettingInfoOperations = ({
  kintoneExportSetting,
  onClickUpdate,
}: UseSettingInfoOperationsProps) => {
  const [input, setInput] = React.useState<UpdateKintoneExportSettingInput>(
    mapSettingToUpdateInput(kintoneExportSetting),
  );
  const [hasChanged, setHasChanged] = React.useState(false);
  const handleChange = React.useCallback(
    (value: UpdateKintoneExportSettingInput) => {
      setInput(value);
      setHasChanged(true);
    },
    [setInput, setHasChanged],
  );

  const [shouldDisplayMappingError, setShouldDisplayMappingError] =
    React.useState(false);

  // refetch された際に updateInput を更新する
  React.useEffect(() => {
    setInput(mapSettingToUpdateInput(kintoneExportSetting));
    setHasChanged(false);
  }, [kintoneExportSetting, setInput, setHasChanged]);

  const [isUpdating, setUpdating] = React.useState(false);
  const handleClickUpdate = React.useCallback(() => {
    setUpdating(true);
    onClickUpdate(input, {
      onSucceeded: (_newSetting) => {
        toast.success("更新しました。");
        setUpdating(false);
      },
      onFailed: (errorMessage) => {
        toast.error(errorMessage);
        setUpdating(false);
      },
    });
  }, [input, onClickUpdate, setUpdating]);

  return {
    handleClickUpdate,
    isUpdating,
    input,
    handleChange,
    shouldDisplayMappingError,
    setShouldDisplayMappingError,
    hasChanged,
  };
};

const mapSettingToUpdateInput = (
  setting: KintoneExportSetting,
): UpdateKintoneExportSettingInput => {
  return {
    id: setting.id,

    kintoneAPIToken: null,

    disabled: setting.disabled,
    name: setting.name,
    description: setting.description,

    elementFieldMappings: setting.elementFieldMappings,
    metadataFieldMapping: setting.metadataFieldMapping,
  };
};

export const useManualExport = (
  hasChanged: boolean,
  kintoneExportSetting: KintoneExportSetting,
  formSchema: IFormSchemaV2,
  onClickExport: ManualExportSubmissionFuncType,
  moveToReportDetail: (reportId: KintoneExportReport["id"]) => void,
  setShouldDisplayMappingError: (shouldDisplay: boolean) => void,
  kintoneFormFields?: KintoneFormFields,
) => {
  const [isExportingModalOpen, setIsExportingModalOpen] = React.useState(false);
  const [isExporting, setExporting] = React.useState(false);
  const [applicationIds, setApplicationIds] = React.useState<string[]>([]);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const handleClickExport = React.useCallback(() => {
    setExporting(true);

    if (hasChanged) {
      toast.error("実行前に、編集中の内容を保存してください");
      setExporting(false);
      return;
    }

    if (
      !validateUpdateSettingInput(
        mapSettingToUpdateInput({
          ...kintoneExportSetting,
          disabled: false, // マッピングが正しいことまで保証するために有効設定とみなして検証する
        }),
        formSchema,
        kintoneFormFields,
      )
    ) {
      toast.error("実行前に、入力内容のエラーを修正してください");
      setExporting(false);
      setShouldDisplayMappingError(true);
      return;
    }

    if (applicationIds.length === 0) {
      setErrorMessage("申請 id を指定してください");
      setExporting(false);
      return;
    }

    if (applicationIds.length > 1000) {
      setErrorMessage("1000件以上の申請 id を指定することはできません。");
      setExporting(false);
      return;
    }

    for (const applicationId of applicationIds) {
      if (!/^\d{19}$/.test(applicationId)) {
        setErrorMessage(
          "申請 id はハイフンを含まない数字19桁で指定してください。",
        );
        setExporting(false);
        return;
      }
    }

    setIsExportingModalOpen(false);

    onClickExport(
      { submissionIds: applicationIds, settingId: kintoneExportSetting.id },
      {
        onSucceeded: (report) => {
          toast.success("連携しました。");
          moveToReportDetail(report.id);
          setExporting(false);
          setApplicationIds([]);
          setErrorMessage(null);
        },
        onFailed: (errorMessage) => {
          toast.error(errorMessage);
          setExporting(false);
        },
        onPolingTimeout: () => {
          toast.info(
            "連携処理は継続中です。連携完了後実行結果は連携実行記録から確認できます。",
            {
              autoClose: false,
            },
          );
          setExporting(false);
        },
      },
    );
  }, [
    kintoneExportSetting,
    formSchema,
    kintoneFormFields,
    setExporting,
    hasChanged,
    applicationIds,
    setApplicationIds,
    onClickExport,
    moveToReportDetail,
    setShouldDisplayMappingError,
    setErrorMessage,
  ]);

  const onChangeApplicationIds = React.useCallback(
    (applicationIds: string[]) => {
      setApplicationIds(applicationIds);
      setErrorMessage(null);
    },
    [setApplicationIds, setErrorMessage],
  );

  const onCloseExportModal = React.useCallback(() => {
    setIsExportingModalOpen(false);
    setApplicationIds([]);
    setErrorMessage(null);
  }, [setApplicationIds, setErrorMessage, setIsExportingModalOpen]);

  return {
    isExporting,
    applicationIds,
    handleClickExport,
    errorMessage,
    onChangeApplicationIds,
    isExportingModalOpen,
    onCloseExportModal,
    setIsExportingModalOpen,
  };
};
